import { useState } from 'react';
import { friendlyMessage } from "@/lib/errors";
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { authApi } from '@/lib/api/services/auth';
import { toast } from '@/hooks/use-toast';
const we_next_logo = '/assets/wenext_logo_dark.png';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const result = await authApi.requestPasswordReset({ email });

      if (result.error) {
        setError(result.error);
      } else {
        setSuccess(true);
        toast({ variant: "success",
          title: "Email sent!",
          description: "Check your email for password reset instructions.",
        });
      }
    } catch (error: any) {
      setError(friendlyMessage(error, 'Failed to send reset email. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex flex-col">
      {/* Header */}
      <header className="p-4 sm:p-6 flex items-center justify-between">
        <div className="flex items-center">
          <img
            src={we_next_logo}
            alt="WeNext Logo"
            className="w-[200px] sm:w-[200px] min-w-[200px]"
          />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Forgot Password</CardTitle>
            <CardDescription>
              Enter your email address and we'll send you a link to reset your password
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <div className="flex flex-col items-center justify-center space-y-4 py-4">
                  <CheckCircle2 className="h-12 w-12 text-green-500" />
                  <div className="text-center">
                    <h3 className="text-lg font-semibold mb-2">Check your email</h3>
                    <p className="text-sm text-muted-foreground">
                      We've sent a password reset link to <strong className="text-foreground">{email}</strong>.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      If you don't see the email, check your spam folder.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      The reset link will expire in 1 hour.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => {
                      setSuccess(false);
                      setEmail('');
                    }}
                    className="w-full"
                    variant="outline"
                  >
                    Send Another Email
                  </Button>
                  <Button
                    onClick={() => navigate('/auth')}
                    className="w-full"
                    variant="outline"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Sign In
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(''); // Clear error when user types
                    }}
                    required
                    autoFocus
                    disabled={loading}
                  />
                </div>

                <Button type="submit" className="w-full" disabled={loading || !email.trim()}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <div className="text-center">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => navigate('/auth')}
                    className="text-sm text-muted-foreground hover:text-primary"
                    disabled={loading}
                  >
                    <ArrowLeft className="mr-1 h-3 w-3" />
                    Back to Sign In
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="p-4 sm:p-6 border-t text-center text-xs sm:text-sm text-muted-foreground">
        <div className="space-y-2">
          <p>© 2025 WeNext. Powered by PhotonX</p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-0 sm:space-x-4">
            <Link
              to="/privacy-policy"
              className="hover:text-primary transition-colors underline"
            >
              Privacy Policy
            </Link>
            <span className="hidden sm:inline">•</span>
            <Link
              to="/terms-of-service"
              className="hover:text-primary transition-colors underline"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

